import { ChangeDetectorRef, Component, ElementRef, EventEmitter, Injector, Input, Optional, Output, QueryList, TemplateRef, ViewChild, ViewChildren } from '@angular/core';
import { FormBuilder, FormControl } from '@angular/forms';
import { MatAutocompleteSelectedEvent, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatDialog } from '@angular/material/dialog';
import { MatSelect } from '@angular/material/select';
import { MatColumnDef, MatTable } from '@angular/material/table';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { Columns, FormStorage, onValueChangeEvent, UserStorage } from '@scisys-uim/uim-common';
import { Asset, ASSET_STATUS_ACTIVE, BUSINESS_UNIT_TYPE, BusinessUnit, COST_CENTRE_TYPE, CostCentre } from 'assets-model';
import { AssetService, AssetTagsService, AssetTypeService, BusinessUnitService, COMPOSED_ASSET_CLASS_CODE, CostCentreService, MechanicalAspectService, RefCodeStorage } from 'assets-service';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { AssetSearchComponentBase } from './asset-search.component.base';
import { AssetType } from 'assets-model';
import { debounceTime } from 'rxjs/operators';

export const ASSET_SEARCH_FILTERS_DEBOUNCE_TIME: number = 200;

export const ASSET_SEARCH_MAINTENANCE_ASPECT_COLUMNS: string[] = ['maintenanceType', 'periodicMaintenance', 'periodicMaintenanceDays', 'routineMaintenance', 'routineMaintenanceDays', 'visualInspection'];
export const ASSET_SEARCH_MECHANICAL_ASPECT_COLUMNS: string[] = ['havs', 'havsRagStatus', 'wbv', 'wbvRagStatus'];
export const ASSET_SEARCH_ELECTRICAL_ASPECT_COLUMNS: string[] = ['portableApplianceTestingDays'];
export const ASSET_SEARCH_ALL_ASPECT_COLUMNS = [...ASSET_SEARCH_MAINTENANCE_ASPECT_COLUMNS, ...ASSET_SEARCH_MECHANICAL_ASPECT_COLUMNS, ...ASSET_SEARCH_ELECTRICAL_ASPECT_COLUMNS];

export const ASSET_SEARCH_RESULT_AUDIT_COLUMNS: string[] = ['createdBy', 'createdDate', 'updatedBy', 'updatedDate'];

export const ASSET_SEARCH_RESULT_DATA_COLUMNS: string[] = ['businessUnit.buId', 'costCentre.ccId', 'location', 'description', 'assetType.assetTypeId', 'identityReference', 'manufacturer', 'model', 'status', 'purchaseDate', 'purchasePrice', 'comments']

export const ASSET_SEARCH_RESULT_MAINTENANCE_COLUMNS: string[] = ['nextRoutineMaintenanceRequiredDate', 'latestRoutineMaintenanceDate'];

export const ASSET_SEARCH_RESULT_DISPLAYED_COLUMNS: string[] = [...ASSET_SEARCH_RESULT_DATA_COLUMNS, ...ASSET_SEARCH_RESULT_MAINTENANCE_COLUMNS, ...ASSET_SEARCH_RESULT_AUDIT_COLUMNS];

export const ASSET_SEARCH_RESULT_DEFAULT_DISABLED_COLUMNS: string[] = ['latestRoutineMaintenanceDate', 'purchasePrice', 'createdBy', 'createdDate', 'updatedBy', 'updatedDate', 'comments'];

export const ASSET_SEARCH_FILTER_DISPLAY_DEFAULT: string[][] = [
  //row1
  ['businessUnit', 'costCentre', 'location', 'assetType', 'description', 'identityReference'],
  //row2
  ['manufacturer', 'model', 'purchaseDateGreaterThanOrEqualTo', 'purchaseDateLessThanOrEqualTo',
    'purchasePriceGreaterThanOrEqualTo', 'purchasePriceLessThanOrEqualTo'],
  //row3
  ['createdBy', 'createdDateGreaterThanOrEqualTo', 'createdDateLessThanOrEqualTo', 'updatedBy',
    'updatedDateGreaterThanOrEqualTo', 'updatedDateLessThanOrEqualTo'],
  //row4
  ['status', 'routineMaintenanceDueSoon'],
  //row5
  ['allTags', 'anyTags']
]

export function assetSearchResultDisplayedColumnsExcluding(excludedColumns: string[]) {
  var columns = [];
  if (excludedColumns == null || excludedColumns.length == 0) {
    return [...ASSET_SEARCH_RESULT_DISPLAYED_COLUMNS];
  }
  for (let column of ASSET_SEARCH_RESULT_DISPLAYED_COLUMNS) {
    if (!excludedColumns.includes(column)) {
      columns.push(column);
    }
  }
  return columns;
}

export function assetSearchResultDisabledColumnsExcluding(excludedColumns: string[]) {
  var columns = [];
  if (excludedColumns == null || excludedColumns.length == 0) {
    return [...ASSET_SEARCH_RESULT_DEFAULT_DISABLED_COLUMNS];
  }
  for (let column of ASSET_SEARCH_RESULT_DEFAULT_DISABLED_COLUMNS) {
    if (!excludedColumns.includes(column)) {
      columns.push(column);
    }
  }
  return columns;
}

@Component({
  selector: 'app-asset-search-result-columns',
  templateUrl: './asset-search-result-columns.component.html',
  styleUrls: ['./asset-search.component.scss']
})
export class AssetSearchResultColumnsComponent {
  @ViewChildren(MatColumnDef) columnDefs: QueryList<MatColumnDef>;

  constructor(@Optional() public table: MatTable<any>, private cdRef: ChangeDetectorRef) { }

  @Input()
  columns: Columns;

  @Input()
  cellClassFn = (element: Asset, fieldName: any) => { };

  @Input()
  assetGetter: (record: any) => Asset;

  @Input()
  useBusinessUnitFullDisplay: boolean = true;

  @Input() 
  useCostCentreFullDisplay: boolean = true;

  ngOnInit() {
    if (!this.assetGetter) {
      this.assetGetter = (record: any) => record;
    }

    if (this.table) {
      this.cdRef.detectChanges();
      this.columnDefs.forEach(columnDef => {
        this.table.addColumnDef(columnDef);
      });
    }
  }

  businessUnitDisplay(item: BusinessUnit, fullDisplay: boolean = this.useBusinessUnitFullDisplay) {
    if (fullDisplay) {
      return this.businessUnitFullDisplay(item);
    } else {
      return this.businessUnitShortDisplay(item);
    }
  }

  businessUnitFullDisplay(item: BusinessUnit) {
    if (item == null) {
      return "";
    }
    if (typeof item == 'string') {
      return item;
    }
    return BUSINESS_UNIT_TYPE.description(item);
  }

  businessUnitShortDisplay(item: BusinessUnit) {
    if (item == null) {
      return "";
    }
    if (typeof item == 'string') {
      return item;
    }
    return item.buId;
  }

  costCentreDisplay(item: CostCentre, fullDisplay: boolean = this.useCostCentreFullDisplay) {
    if (fullDisplay) {
      return this.costCentreFullDisplay(item);
    } else {
      return this.costCentreShortDisplay(item);
    }
  }

  costCentreFullDisplay(item: CostCentre) {
    if (item == null) {
      return "";
    }
    if (typeof item == 'string') {
      return item;
    }
    return COST_CENTRE_TYPE.description(item);
  }

  costCentreShortDisplay(item: CostCentre) {
    if (item == null) {
      return "";
    }
    if (typeof item == 'string') {
      return item;
    }
    return item.ccId;
  }


  cellClass(element: Asset, fieldName: string) {
    return this.cellClassFn(element, fieldName);
  }
}


@Component({
  selector: 'app-asset-search-filters',
  templateUrl: './asset-search-filters.component.html',
  styleUrls: ['./asset-search.component.scss']
})
export class AssetSearchFiltersComponent extends AssetSearchComponentBase {

  @ViewChild('businessUnitTemplate', { static: true }) businessUnitTemplate: TemplateRef<any>;
  @ViewChild('costCentreTemplate', { static: true }) costCentreTemplate: TemplateRef<any>;
  @ViewChild('locationTemplate', { static: true }) locationTemplate: TemplateRef<any>;
  @ViewChild('purchaseDateGreaterThanOrEqualToTemplate', { static: true }) purchaseDateGreaterThanOrEqualToTemplate: TemplateRef<any>;
  @ViewChild('purchaseDateLessThanOrEqualToTemplate', { static: true }) purchaseDateLessThanOrEqualToTemplate: TemplateRef<any>;
  @ViewChild('purchasePriceGreaterThanOrEqualToTemplate', { static: true }) purchasePriceGreaterThanOrEqualToTemplate: TemplateRef<any>;
  @ViewChild('purchasePriceLessThanOrEqualToTemplate', { static: true }) purchasePriceLessThanOrEqualToTemplate: TemplateRef<any>;
  @ViewChild('assetTypeTemplate', { static: true }) assetTypeTemplate: TemplateRef<any>;
  @ViewChild('manufacturerTemplate', { static: true }) manufacturerTemplate: TemplateRef<any>;
  @ViewChild('modelTemplate', { static: true }) modelTemplate: TemplateRef<any>;
  @ViewChild('descriptionTemplate', { static: true }) descriptionTemplate: TemplateRef<any>;
  @ViewChild('identityReferenceTemplate', { static: true }) identityReferenceTemplate: TemplateRef<any>;
  @ViewChild('statusTemplate', { static: true }) statusTemplate: TemplateRef<any>;
  @ViewChild('createdByTemplate', { static: true }) createdByTemplate: TemplateRef<any>;
  @ViewChild('createdDateGreaterThanOrEqualToTemplate', { static: true }) createdDateGreaterThanOrEqualToTemplate: TemplateRef<any>;
  @ViewChild('createdDateLessThanOrEqualToTemplate', { static: true }) createdDateLessThanOrEqualToTemplate: TemplateRef<any>;
  @ViewChild('updatedByTemplate', { static: true }) updatedByTemplate: TemplateRef<any>;
  @ViewChild('updatedDateGreaterThanOrEqualToTemplate', { static: true }) updatedDateGreaterThanOrEqualToTemplate: TemplateRef<any>;
  @ViewChild('updatedDateLessThanOrEqualToTemplate', { static: true }) updatedDateLessThanOrEqualToTemplate: TemplateRef<any>;
  @ViewChild('routineMaintenanceDueSoonTemplate', { static: true }) routineMaintenanceDueSoonTemplate: TemplateRef<any>;
  @ViewChild('allTagsTemplate', { static: true }) allTagsTemplate: TemplateRef<any>;
  @ViewChild('anyTagsTemplate', { static: true }) anyTagsTemplate: TemplateRef<any>;

  private _filterDisplay: string[][] = ASSET_SEARCH_FILTER_DISPLAY_DEFAULT;
  private filterTemplateMap: { [key: string]: TemplateRef<any> } = {};
  private _disabledFilters: string[] = [];

  get filterDisplay() {
    return this._filterDisplay;
  }
  @Input()
  set filterDisplay(value: string[][]) {
    if (value) {
      this._filterDisplay = this.filterFilterDisplay(value);
    } else {
      this._filterDisplay = [];
    }
  }

  get disabledFilters() {
    if (this._disabledFilters == null) {
      this._disabledFilters = [];
    }
    return this._disabledFilters;
  }
  @Input()
  set disabledFilters(value: string[]) {
    this._disabledFilters = value;
    this.filterDisplay = this.filterDisplay;
  }

  fullTagList: string[] = [];

  allTagsInputField: string;
  allTagsFilter: string[] = [];
  @ViewChild('allTags') allTagsInput: ElementRef;
  private allTagsSelectionListSubject: Subject<string[]> = new BehaviorSubject([]);
  allTagsSelectionList: Observable<String[]> = this.allTagsSelectionListSubject.asObservable();
  @ViewChild('allTags', { read: MatAutocompleteTrigger }) allTagsAutocompleteTrigger: MatAutocompleteTrigger;

  anyTagsInputField: string;
  anyTagsFilter: string[] = [];
  @ViewChild('anyTags') anyTagsInput: ElementRef;
  private anyTagsSelectionListSubject: Subject<string[]> = new BehaviorSubject([]);
  anyTagsSelectionList: Observable<String[]> = this.anyTagsSelectionListSubject.asObservable();
  @ViewChild('anyTags', { read: MatAutocompleteTrigger }) anyTagsAutocompleteTrigger: MatAutocompleteTrigger;

  @Input('composedAssetClass') assetClass: COMPOSED_ASSET_CLASS_CODE = "Asset";

  routineMaintenanceDueSoonFilter: boolean;
  @ViewChild('routineMaintenanceDueSoonFilterInput') routineMaintenanceDueSoonFilterInput: MatSelect;

  readyForSearch: boolean = false;
  isReadyForSearch(): boolean {
    return this.readyForSearch;
  }

  constructor(protected route: ActivatedRoute,
    protected router: Router,
    protected userStorage: UserStorage,
    protected dialog: MatDialog,
    protected assetService: AssetService,
    protected assetTagsService: AssetTagsService,
    protected businessUnitService: BusinessUnitService,
    protected costCentreService: CostCentreService,
    protected mechanicalAspectService: MechanicalAspectService,
    protected assetTypeService: AssetTypeService,
    protected refCodeStorage: RefCodeStorage,
    protected fb: FormBuilder,
    protected formStorage: FormStorage,
    protected injector: Injector) {
    super(route, router, userStorage, dialog, assetService, assetTypeService, businessUnitService, costCentreService, mechanicalAspectService, refCodeStorage, fb, formStorage, injector);
    this.searchForm.addControl('allTags', this.fb.control(null));
    this.searchForm.addControl('anyTags', this.fb.control(null));
    this.searchForm.addControl('routineMaintenanceDueSoon', this.fb.control(null));
  }

  addDisabledFilter(filter: string) {
    if (!this.isDisabledFilter(filter)) {
      this.disabledFilters.push(filter);
    }
  }

  isDisabledFilter(filter: string): boolean {
    return this.disabledFilters.includes(filter);
  }

  isShowFilter(filter: string): boolean {
    return !this.disabledFilters.includes(filter);
  }

  ngAfterViewInit(): void {
    super.ngAfterViewInit();
    this.wireAllTagsAutocomplete();
    this.wireAnyTagsAutocomplete();
    this.populateFilterTemplateMap();
    this.filterDisplay = ASSET_SEARCH_FILTER_DISPLAY_DEFAULT;
  }



  createServiceArguments(): any {
    var args = super.createServiceArguments();
    //important these create new list instances or the previousServiceArguments will hold a 
    //reference to the actual list instead of the most recent copy, and make searches fail
    args['containsAllTags'] = [...this.allTagsFilter];
    args['containsAnyTags'] = [...this.anyTagsFilter];
    args['routineMaintenanceDueSoon'] = this.routineMaintenanceDueSoonFilter;
    return args;
  }

  clearFilters(): void {
    this.allTagsFilter = [];
    this.allTagsInputField = null;
    this.anyTagsFilter = [];
    this.anyTagsInputField = null;
    this.routineMaintenanceDueSoonFilter = null;
    super.clearFilters();
  }

  @Output('serviceArguments')
  serviceArgumentsChange: EventEmitter<any> = new EventEmitter<any>();

  loadPage(): void {
    if (this.isReadyForSearch()) {
      this.serviceArgumentsChange.emit(this.createServiceArguments());
    }
  }

  maybeDoAutoSearch(): void {
    this.triggerSearch();
  }

  protected onInitAsyncComplete() {
    // this.composedAssetClassLoadSelectionList();
    this.businessUnitLoadSelectionList();
    this.costCentreLoadSelectionList();
    this.tagsLoadSelectionList();
    this.assetTypeLoadSelectionList();
    this.statusLoadSelectionList();


    if (!this.restoreFormFromParams(this.searchForm)) {
      this.resetSearchForm();
      this.restoreFormChanges();
    }

    if (this.assetClass == 'Asset') {
      this.statusFilter = this.statusFilter || ASSET_STATUS_ACTIVE;
    }

    this.readyForSearch = true;
    super.onInitAsyncComplete();
  }

  resetSearchForm() {
    // placeholder
  }

  costCentreLoadSelectionList() {
    if (this.businessUnitFilter != null) {
      this.costCentreService.findAllByBusinessUnit(this.businessUnitFilter?.buId).subscribe(
        data => {
          this.costCentreSelectionList = data.items;
          this.costCentreFilter = null;
          this.costCentre.enable();
        }
      );
    } else {
      this.costCentreSelectionList = [];
      this.costCentreFilter = null;
      this.costCentre.disable();
    }
  }

  // TODO - Having to explicitly add a change handler for each filter is not ideal.
  // The base class ngAfterViewInit calls to wire the listeners are failing because the filters are not yet created.
  // This could probably be fixed with @ViewChildren query, and change listener on the query, when someone has time to look.  If this approach is ever ported to the generator - Please spend more time fixing, rather than using this explicit coding approach.

  businessUnitChanged(value: BusinessUnit, unsetDependents: boolean = true) {
    super.businessUnitChanged(value, unsetDependents);
    this.costCentreLoadSelectionList();
    this.maybeDoAutoSearch();
  }

  costCentreChanged(value: CostCentre, unsetDependents?: boolean): void {
    super.costCentreChanged(value, unsetDependents);
    this.maybeDoAutoSearch();
  }

  locationChanged(value: string, unsetDependents?: boolean): void {
    super.locationChanged(value, unsetDependents);
    this.maybeDoAutoSearch();
  }

  assetTypeChanged(value: AssetType, unsetDependents?: boolean): void {
    super.assetTypeChanged(value, unsetDependents);
    this.maybeDoAutoSearch();
  }

  descriptionChanged(value: string, unsetDependents?: boolean): void {
    super.descriptionChanged(value, unsetDependents);
    this.maybeDoAutoSearch();
  }

  identityReferenceChanged(value: string, unsetDependents?: boolean): void {
    super.identityReferenceChanged(value, unsetDependents);
    this.maybeDoAutoSearch();
  }

  manufacturerChanged(value: string, unsetDependents?: boolean): void {
    super.manufacturerChanged(value, unsetDependents);
    this.maybeDoAutoSearch();
  }

  modelChanged(value: string, unsetDependents?: boolean): void {
    super.modelChanged(value, unsetDependents);
    this.maybeDoAutoSearch();
  }

  purchaseDateGreaterThanOrEqualToChanged(value: Date, unsetDependents?: boolean): void {
    super.purchaseDateGreaterThanOrEqualToChanged(value, unsetDependents);
    this.maybeDoAutoSearch();
  }

  purchaseDateLessThanOrEqualToChanged(value: Date, unsetDependents?: boolean): void {
    super.purchaseDateLessThanOrEqualToChanged(value, unsetDependents);
    this.maybeDoAutoSearch();
  }

  purchasePriceGreaterThanOrEqualToChanged(value: number, unsetDependents?: boolean): void {
    super.purchasePriceGreaterThanOrEqualToChanged(value, unsetDependents);
    this.maybeDoAutoSearch();
  }

  purchasePriceLessThanOrEqualToChanged(value: number, unsetDependents?: boolean): void {
    super.purchasePriceLessThanOrEqualToChanged(value, unsetDependents);
    this.maybeDoAutoSearch();
  }

  statusChanged(value: string, unsetDependents?: boolean): void {
    super.statusChanged(value, unsetDependents);
    this.maybeDoAutoSearch();
  }

  createdByChanged(value: string, unsetDependents?: boolean): void {
    super.createdByChanged(value, unsetDependents);
    this.maybeDoAutoSearch();
  }

  createdDateGreaterThanOrEqualToChanged(value: Date, unsetDependents?: boolean): void {
    super.createdDateGreaterThanOrEqualToChanged(value, unsetDependents);
    this.maybeDoAutoSearch();
  }

  createdDateLessThanOrEqualToChanged(value: Date, unsetDependents?: boolean): void {
    super.createdDateLessThanOrEqualToChanged(value, unsetDependents);
    this.maybeDoAutoSearch();
  }

  updatedByChanged(value: string, unsetDependents?: boolean): void {
    super.updatedByChanged(value, unsetDependents);
    this.maybeDoAutoSearch();
  }

  updatedDateGreaterThanOrEqualToChanged(value: Date, unsetDependents?: boolean): void {
    super.updatedDateGreaterThanOrEqualToChanged(value, unsetDependents);
    this.maybeDoAutoSearch();
  }

  updatedDateLessThanOrEqualToChanged(value: Date, unsetDependents?: boolean): void {
    super.updatedDateLessThanOrEqualToChanged(value, unsetDependents);
    this.maybeDoAutoSearch();
  }

  tagsLoadSelectionList() {
    this.assetTagsService.findAllTagsForAssetClass(this.assetClass).subscribe(
      data => {
        this.fullTagList = data.sort();
        this.allTagsSelectionListFilter(null);
        this.anyTagsSelectionListFilter(null);
      }
    );
  }

  routineMaintenanceDueSoonChanging(value: boolean, unsetDependents: boolean = true) { }

  routineMaintenanceDueSoonChanged(value: boolean, unsetDependents: boolean = true) {
    this.maybeDoAutoSearch();
  }

  wireAllTagsAutocomplete(): void {
    if (this.allTags) {
      onValueChangeEvent(this.allTags, value => {
        if (value == "") {
          value = null;
          this.allTagsInputField = null;
        }
        this.allTagsSelectionListFilter(value);
      }, {
        debounceMillis: 100
      });
    }
  }

  selectedAllTagClick(event: MatAutocompleteSelectedEvent): void {
    var tag: string = this.fullTagList.find(p => p === event.option.value);
    if (tag != null) {
      this.selectedAllTag(tag);
    }
  }

  selectedAllTag(tag: string): void {
    if (this.allTagsFilter) {
      this.allTagsFilter.push(tag);
    } else {
      this.allTagsFilter = [tag];
    }
    this.allTagsInput.nativeElement.value = '';
    this.allTags.setValue(null);
    this.allTagsSelectionListFilter(null);
    this.maybeDoAutoSearch();
  }

  selectedAllTagEnter() {
    var tag: string = this.fullTagList.find(p => this.assetTagsService.matchingTagExact(p, this.allTagsInputField));
    if (tag == null) {
      var tagList = this.allTagsFilteredList(this.allTagsInputField);
      if (tagList != null && tagList.length >= 1) {
        tag = tagList[0];
      }
    }
    if (tag != null) {
      this.selectedAllTag(tag);
    }
  }

  allTagsFilteredList(text: string) {
    var filtered = this.fullTagList ? this.fullTagList.filter(p => !(this.allTagsFilter || []).find(o => o == p)) : [];
    if (text != null && text.length > 0) {
      filtered = filtered.filter(p => this.assetTagsService.matchingTagPartial(text, p));
    }
    return filtered;
  }

  allTagsSelectionListFilter(text: string): void {
    var filtered = this.allTagsFilteredList(text);
    this.allTagsSelectionListSubject.next(filtered);
  }

  removeAllTag(tag: string): void {
    const index = this.allTagsFilter.indexOf(tag);

    if (index >= 0) {
      this.allTagsFilter.splice(index, 1);
      this.allTagsSelectionListFilter(null);
      this.maybeDoAutoSearch();
    }
  }

  wireAnyTagsAutocomplete(): void {
    if (this.anyTags) {
      onValueChangeEvent(this.anyTags, value => {
        if (value == "") {
          value = null;
          this.anyTagsInputField = null;
        }
        this.anyTagsSelectionListFilter(value);
      }, {
        debounceMillis: 100
      });
    }
  }

  selectedAnyTagClick(event: MatAutocompleteSelectedEvent): void {
    var tag: string = this.fullTagList.find(p => p === event.option.value);
    if (tag != null) {
      this.selectedAnyTag(tag);
    }
  }

  selectedAnyTag(tag: string): void {
    if (this.anyTagsFilter) {
      this.anyTagsFilter.push(tag);
    } else {
      this.anyTagsFilter = [tag];
    }
    this.anyTagsInput.nativeElement.value = '';
    this.anyTags.setValue(null);
    this.anyTagsSelectionListFilter(null);
    this.maybeDoAutoSearch();
  }

  selectedAnyTagEnter() {
    var tag: string = this.fullTagList.find(p => this.assetTagsService.matchingTagExact(p, this.anyTagsInputField));
    if (tag == null) {
      var tagList = this.anyTagsFilteredList(this.anyTagsInputField);
      if (tagList != null && tagList.length >= 1) {
        tag = tagList[0];
      }
    }
    if (tag != null) {
      this.selectedAnyTag(tag);
    }
  }

  anyTagsFilteredList(text: string) {
    var filtered = this.fullTagList ? this.fullTagList.filter(p => !(this.anyTagsFilter || []).find(o => o == p)) : [];
    if (text != null && text.length > 0) {
      filtered = filtered.filter(p => this.assetTagsService.matchingTagPartial(text, p));
    }
    return filtered;
  }

  anyTagsSelectionListFilter(text: string): void {
    var filtered = this.anyTagsFilteredList(text);
    this.anyTagsSelectionListSubject.next(filtered);
  }

  removeAnyTag(tag: string): void {
    const index = this.anyTagsFilter.indexOf(tag);

    if (index >= 0) {
      this.anyTagsFilter.splice(index, 1);
      this.anyTagsSelectionListFilter(null);
      this.maybeDoAutoSearch();
    }
  }

  templateNameFromFilter(filterName: string): string {
    const sfx = 'Template';
    return filterName + sfx;
  }

  populateFilterTemplateMap(): void {
    for (let filterGroup of this.filterDisplay) {
      for (let filter of filterGroup) {
        const templateName = this.templateNameFromFilter(filter)
        this.filterTemplateMap[templateName] = this[templateName];
      }
    }
  }

  filterFilterDisplay(filterDisplay: string[][]): string[][] {
    var cleaned = [];
    for (let filterGroup of filterDisplay) {
      if (filterGroup.length <= 0) {
        continue;
      }
      var cleanedGroup = [];
      for (let filter of filterGroup) {
        if (!this.isShowFilter(filter)) {
          continue;
        }
        cleanedGroup.push(filter);
      }
      cleaned.push(cleanedGroup);
    }
    return cleaned
  }

  getFilterTemplate(filterName: string): TemplateRef<any> {
    return this.filterTemplateMap[this.templateNameFromFilter(filterName)];
  }

  get anyTags() {
    return this.searchForm.get('anyTags');
  }

  get allTags() {
    return this.searchForm.get('allTags');
  }

  get routineMaintenanceDueSoon() {
    return this.searchForm.get('routineMaintenanceDueSoon');
  }

  protected transformAllPropertiesToModelSetters(): {
    [key: string]: (value: any, formControl: FormControl, formControlName: string) => void
  } {
    const self = this;
    var transformer = super.transformAllPropertiesToModelSetters();
    transformer['routineMaintenanceDueSoon'] = (value: string) => {
      self.routineMaintenanceDueSoonFilter = value == 'true' ? true : (value == 'false' ? false : null);
    };
    return transformer;
  }

  protected transformParamsToModelSetters(params: Params): {
    [key: string]: (value: any, formControl: FormControl, formControlName: string) => void
  } {
    return this.transformAllPropertiesToModelSetters();
  }
}



@Component({
  selector: 'app-asset-search',
  templateUrl: './asset-search.component.html',
  styleUrls: ['./asset-search.component.scss']
})
export class AssetSearchComponent extends AssetSearchComponentBase {

  displayedColumns: string[] = ['assetId', ...ASSET_SEARCH_RESULT_DISPLAYED_COLUMNS, 'actions'];
  disabledColumns: string[] = [...ASSET_SEARCH_RESULT_DEFAULT_DISABLED_COLUMNS]

  isReadyForSearch(): boolean {
    return this.filtersComponent?.isReadyForSearch();
  }

  constructor(protected route: ActivatedRoute,
    protected router: Router,
    protected userStorage: UserStorage,
    protected dialog: MatDialog,
    protected assetService: AssetService,
    protected assetTypeService: AssetTypeService,
    protected businessUnitService: BusinessUnitService,
    protected costCentreService: CostCentreService,
    protected mechanicalAspectService: MechanicalAspectService,
    protected refCodeStorage: RefCodeStorage,
    protected fb: FormBuilder,
    protected formStorage: FormStorage,
    protected injector: Injector) {
    super(route, router, userStorage, dialog, assetService, assetTypeService, businessUnitService, costCentreService, mechanicalAspectService, refCodeStorage, fb, formStorage, injector);
  }

  @ViewChild(AssetSearchFiltersComponent) filtersComponent: AssetSearchFiltersComponent;

  protected onInitAsyncComplete() {
    if (!this.restoreFormFromParams(this.searchForm)) {
      this.resetSearchForm();
      this.restoreFormChanges();
    }
    super.onInitAsyncComplete();
  }

  ngAfterViewInit(): void {
    super.ngAfterViewInit();
    //debounce this on search side as 
    this.filtersComponent?.serviceArgumentsChange.pipe(
      debounceTime(ASSET_SEARCH_FILTERS_DEBOUNCE_TIME)
    ).subscribe(args => {
      this.maybeDoAutoSearch();
    });
    this.maybeDoAutoSearch();
  }

  resetSearchForm() {
    this.filtersComponent?.resetSearchForm();
  }

  clearFilters() {
    super.clearFilters();
    this.filtersComponent?.clearFilters();
  }

  createServiceArguments() {
    return this.filtersComponent?.createServiceArguments() || <any>{};
  }

  protected transformParamsToModelSetters(params: Params): {
    [key: string]: (value: any, formControl: FormControl, formControlName: string) => void
  } {
    return this.transformAllPropertiesToModelSetters();
  }

}



